import * as React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { API_GuessTable } from "../../../src/apidefs";
import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";

type GuessTableParams = {
  id: string;
};

export const GuessTablePage = (): JSX.Element => {
  const { id } = useParams<GuessTableParams>();
  const [guessTable, setGuessTable] = useState<API_GuessTable>();

  useEffect(() => {
    const loadData = async () => {
      const guessTableResponse = await api.get<"/api/compo/:id/guesstable">(
        `/api/compo/${Number(id)}/guesstable`
      );

      if (!api.isToast(guessTableResponse)) {
        setGuessTable(guessTableResponse);
      }
    };

    loadData();
  }, [id]);

  if (!guessTable) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 px-5 py-8 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          Loading guessing table...
        </div>
      </div>
    );
  }

  // Calculate correct guesses per user (column totals)
  const correctByUser: Record<string, number> = {};
  for (const user of guessTable.users) {
    const guesses = guessTable.table[user] || [];
    correctByUser[user] = guesses.filter((g) => g.correct).length;
  }

  // Sort users by correct guesses (most correct first)
  const sortedUsers = [...guessTable.users].sort(
    (a, b) => correctByUser[b] - correctByUser[a]
  );

  // Calculate correct guesses per entry (row totals)
  const correctByEntry: Record<string, number> = {};
  for (const entry of guessTable.entries) {
    let count = 0;
    for (const user of guessTable.users) {
      const guesses = guessTable.table[user] || [];
      const guess = guesses.find((g) => g.entry === entry.title);
      if (guess?.correct) count++;
    }
    correctByEntry[entry.title] = count;
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="px-5 py-8 sm:px-10 md:px-20 lg:px-32 xl:px-[200px]">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Guessing Table</h1>
          <RLink to={`/compo/${id}/results`} variant="secondary">
            &larr; Back to results
          </RLink>
        </div>

        {/* Table Card */}
        <div className="rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-700">
                  <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-700 px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-200 min-w-[200px]">
                    Entry
                  </th>
                  {sortedUsers.map((user) => (
                    <th
                      key={user}
                      className="px-4 py-3 text-center text-sm font-semibold text-slate-700 dark:text-slate-200 min-w-[120px]"
                    >
                      <RLink to={`/user/${user}`}>{user}</RLink>
                      <div className="mt-1 text-xs font-normal text-emerald-600 dark:text-emerald-400">
                        {correctByUser[user]} correct
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {guessTable.entries.map((entry) => {
                  return (
                    <tr
                      key={entry.id}
                      className="border-b border-slate-100 bg-white dark:border-white/5 dark:bg-slate-800"
                    >
                      <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-100">
                        <div className="flex items-center justify-between gap-2">
                          <div
                            className="max-w-[180px] truncate"
                            title={entry.title}
                          >
                            <RLink to={`/entry/${entry.id}`}>
                              {entry.title}
                            </RLink>
                          </div>
                          {correctByEntry[entry.title] > 0 && (
                            <span className="shrink-0 text-xs text-emerald-600 dark:text-emerald-400">
                              {correctByEntry[entry.title]}
                            </span>
                          )}
                        </div>
                      </td>
                      {sortedUsers.map((user) => {
                        const result =
                          guessTable.table[user] &&
                          guessTable.table[user].find(
                            ({ entry: entryTitle }) =>
                              entryTitle === entry.title
                          );

                        return (
                          <td
                            key={user}
                            className="px-4 py-3 text-center text-sm"
                          >
                            {result ? (
                              <div className="flex items-center justify-center gap-1.5">
                                {result.correct ? (
                                  <CheckIcon className="h-4 w-4 text-emerald-500" />
                                ) : (
                                  <XMarkIcon className="h-4 w-4 text-red-400" />
                                )}
                                <RLink
                                  to={`/user/${result.username}`}
                                  className={
                                    result.correct
                                      ? "text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                                      : "text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                                  }
                                >
                                  {result.username}
                                </RLink>
                              </div>
                            ) : (
                              <span className="text-slate-300 dark:text-slate-600">
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1.5">
            <CheckIcon className="h-4 w-4 text-emerald-500" />
            <span>Correct guess</span>
          </div>
          <div className="flex items-center gap-1.5">
            <XMarkIcon className="h-4 w-4 text-red-400" />
            <span>Incorrect guess</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-300 dark:text-slate-600">—</span>
            <span>No guess</span>
          </div>
        </div>
      </div>
    </div>
  );
};
